<template>
    <div class="home-header-wrapper" :class="{'mobile': getMobileAppState, 'with-error': applyPatrimonyErrorState}">
        <!-- *********************************************
        *                                                *
        *                Welcome and Amounts             *
        *                                                *
        ********************************************** -->
        <div class="left-content">
            <!-- *********************************************
            *                                                *
            *                   Top Wrapper                  *
            *                                                *
            ********************************************** -->
            <div class="top-wrapper">
                <!-- Welcome message -->
                <div class="welcome-message">
                    {{t('home.welcomeMessage1')}} <span class="name">{{firstName}}</span><icon-hand/><span v-if="getPatrimonyInfo && !applyPatrimonyErrorState">{{t('home.welcomeMessage2')}}</span>
                </div>
                <!-- Assets/Liabilities Dropdown -->
                <dropdown-generic
                v-if="getPatrimonyInfo && !applyPatrimonyErrorState"
                :params="{}"
                :options="assetsLiabilitesDropOptions"
                :selectedOption="assetsLiabilitesSelected"
                @clicked="assetsLiabilitesSelected = $event"/>
            </div>
            <!-- *********************************************
            *                                                *
            *                  Amount Wrapper                *
            *                                                *
            ********************************************** -->
            <div v-if="getPatrimonyInfo && !applyPatrimonyErrorState" class="amount-wrapper">
                <!-- Show/Hide Amounts -->
                <button-secondary
                buttonStyle="round"
                :icon="hideAmounts ? 'eye-open' : 'eye-close'"
                @clicked="[hideAmounts = !hideAmounts, emit('hideAmounts', hideAmounts)]"/>
                <!-- Amount -->
                <div class="amount">{{amountFormat(hideAmounts ? '***,00' : totalAmount, selectedCurrency)}}</div>
                <!-- Currencies Dropdown -->
                <dropdown-more-options
                v-if="getPatrimonyInfo && currencyOptions.length > 1 && !applyPatrimonyErrorState"
                icon="arrow-down"
                :options="currencyOptions"
                :selectedOption="selectedCurrency"
                @clicked="selectedCurrency = $event"/>
            </div>
            <!-- Error on getting patrimony info -->
            <infobox-generic v-if="applyPatrimonyErrorState" state="negative" :label="t('home.errorAssetsLiabilities')"/>
        </div>
        <!-- *********************************************
        *                                                *
        *             Operations to Sign Button          *
        *                                                *
        ********************************************** -->
        <slot name="operations-header-button-slot"></slot>
    </div>
</template>

<script setup>
import IconHand from '@/assets/icons/hand.svg?inline'
import InfoboxGeneric from '@/components/generic/infobox/Generic'
import ButtonSecondary from '@/components/generic/buttons/Secondary'
import DropdownGeneric from '@/components/generic/dropdowns/generic/Generic'
import DropdownMoreOptions from '@/components/generic/dropdowns/moreOptions/MoreOptions'
/* Vue */
import { ref, computed } from 'vue'
/* Assets */
import { amountFormat } from '@/assets/scripts/amounts'
/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['hideAmounts'])

/* Props
@applyPatrimonyErrorState {Boolean} prop to tell widget patrimony fetch state
*/
const props = defineProps({
    applyPatrimonyErrorState: Boolean
})

/* mapActions & mapGetters */
const { getAllCustomerInfo } = mapGetters(moduleMapper.SESSION)
const { getPatrimonyInfo } = mapGetters(moduleMapper.HOME)
const { getMobileAppState } = mapGetters()

const assetsLiabilitesSelected = ref('ASSET')
const assetsLiabilitesDropOptions = [{
    id: 'ASSET',
    text: t('home.assets')
}, {
    id: 'LIABILITY',
    text: t('home.liabilities')
}]

const hideAmounts = ref(true)
const selectedCurrency = ref('AKZ')
emit('hideAmounts', true)

const currencyOptions = computed(() => {
    return getPatrimonyInfo.value.currenciesFromAccounts.map(currency => {
        return {
            id: currency.name,
            text: currency.name === 'AKZ' ? 'Kz' : currency.name,
            selected: currency.name === selectedCurrency.value
        }
    })
})

const totalAmount = computed(() => {
    return assetsLiabilitesSelected.value === 'ASSET' ? getPatrimonyInfo.value.totalAssetsAvailableBalanceByCurrency[selectedCurrency.value] || 0 : getPatrimonyInfo.value.totalLiabilitiesAvailableBalanceByCurrency[selectedCurrency.value] || 0
})

const firstName = computed(() => {
    return getAllCustomerInfo.value.displayName.split(' ')[0]
})
</script>

<style lang="scss" scoped>
.home-header-wrapper {
    width: 100%;
    height: 96px;
    max-width: 1636px; /* Max size set by design on breakpoints file */
    margin: 0 auto 24px auto; /* To align content on center */
    padding: 0 24px;

    display: flex;
    justify-content: space-between;
    position: relative;

    .top-wrapper {
        width: fit-content;
        height: 40px;
        margin-bottom: 16px;

        display: flex;
        align-items: center;

        .welcome-message {
            width: auto;
            flex-shrink: 0;
            margin-right: 8px;

            display: flex;
            gap: 4px;

            @include H5-Regular;
            color: var(--secondary);

            .name {
                text-transform: capitalize;
                color: var(--secondary-dark-2);
            }
        }

        .dropdown-wrapper {
            width: 122px;

            :deep(.dropdown-animation-wrapper) {
                min-width: 110px;
            }
        }
    }

    .amount-wrapper {
        width: auto;

        display: flex;
        align-items: center;

        .button-secondary {
            margin-right: 16px;
        }

        .amount {
            width: fit-content;
            flex-shrink: 0;

            @include H2-Medium;
            color: var(--secondary-dark-3);
        }
    }

    &.mobile {
        height: 146px;
        margin-bottom: 0;
        padding: 16px;

        background-color: var(--secondary-light-3);
        border-radius: 16px 16px 0px 0px;

        &.with-error {
            height: 168px;
        }

        &:after {
            content: '';
            width: 100%;
            height: 16px;

            position: absolute;
            bottom: 0;
            left: 0;

            background-color: var(--secondary-light-4);
            border-radius: 16px 16px 0px 0px;
        }

        .top-wrapper {
            width: 100%;
            justify-content: space-between;
        }
        .amount-wrapper {
            .amount {
                @include H4-Medium;
            }
        }
    }
}
</style>
